<template>
  <b-modal
    v-model="visible"
    centered
    :title="$t('pamm_early_exit_title', { name: accountName })"
    :no-close-on-backdrop="confirmPending"
    :no-close-on-esc="confirmPending"
    :hide-header-close="confirmPending"
    @hidden="reset"
  >
    <template v-if="quote">
      <p class="text-muted">
        {{ $t('pamm_early_exit_description') }}
      </p>
      <dl class="row mb-0">
        <template v-for="row in rows">
          <dt
            :key="`${row.key}-label`"
            class="col-7 font-weight-normal text-muted"
          >
            {{ $t(row.label) }}
          </dt>
          <dd
            :key="`${row.key}-value`"
            class="col-5 font-weight-bold text-nowrap"
            :class="row.colored ? valueClass(row.value) : ''"
          >
            <span dir="ltr">{{ formatAmount(row.value, row.signed) }}</span>
          </dd>
        </template>
      </dl>
      <div class="pamm-exit__payout d-flex align-items-center justify-content-between mt-1">
        <span class="font-weight-bold">{{ $t('pamm_early_exit_payout') }}</span>
        <h3 class="mb-0 font-weight-bolder text-success">
          <span dir="ltr">{{ formatAmount(quote.payout) }}</span>
        </h3>
      </div>
      <b-alert
        show
        :variant="quote.fineWaived ? 'success' : 'warning'"
        class="p-1 mt-1 mb-0"
      >
        {{ quote.fineWaived ? $t('pamm_early_exit_no_fine') : $t('pamm_early_exit_fine_note', { days: formatDays(quote.remainingDays) }) }}
      </b-alert>
      <b-alert
        v-if="expired"
        show
        variant="danger"
        class="p-1 mt-1 mb-0"
      >
        {{ $t('pamm_early_exit_expired') }}
      </b-alert>
      <small
        v-else
        class="d-block text-muted mt-1"
      >
        {{ $t('pamm_early_exit_valid_for', { time: countdown }) }}
      </small>
    </template>

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        :disabled="confirmPending"
        @click="visible = false"
      >
        {{ $t('cancel') }}
      </b-button>
      <b-button
        v-if="expired"
        variant="primary"
        :disabled="refreshPending"
        @click="refreshQuote"
      >
        <b-spinner
          v-if="refreshPending"
          small
          type="grow"
        />
        <span v-else>{{ $t('pamm_early_exit_refresh') }}</span>
      </b-button>
      <b-button
        v-else
        variant="danger"
        :disabled="!quote || confirmPending"
        @click="confirm"
      >
        <b-spinner
          v-if="confirmPending"
          small
          type="grow"
        />
        <span v-else>{{ $t('pamm_early_exit_confirm') }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  BSpinner,
  BAlert,
} from 'bootstrap-vue'
import PammApis from '@/modules/pamm/service/apis'
import { numberWithCommas } from '@/utils/index'

const pammApis = new PammApis()

// how long PAMM honours a quote, when the response does not say
const QUOTE_VALIDITY_MS = 5 * 60 * 1000
// time left for the confirm request to reach PAMM before the quote runs out
const CONFIRM_MARGIN_MS = 15 * 1000

const QUOTE_FIELDS = [
  'payout', 'valueBeforeFine', 'cancelationFine', 'fineWaived', 'grossShare', 'managementFee',
  'performanceFee', 'stake', 'remainingDays', 'asOf', 'expiresAt', 'quoteToken',
]
const snakeCase = key => key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

// The cabin api may pass PAMM's quote through as it is or in snake_case, in `results` or
// in the `data` of PAMM's own envelope.
const readQuote = res => {
  const body = res?.data ?? {}
  const raw = [body.results?.data, body.results, body.data, body]
    .find(candidate => candidate && typeof candidate === 'object' && ('payout' in candidate))
  if (!raw) return null
  return Object.fromEntries(QUOTE_FIELDS.map(key => [key, raw[key] ?? raw[snakeCase(key)]]))
}

/**
 * Early exit from one investment. The parent calls `open(investorId, accountName)`: the
 * quote is fetched first and the dialog opens only once it has arrived.
 */
export default {
  components: {
    BModal,
    BButton,
    BSpinner,
    BAlert,
  },
  data() {
    return {
      visible: false,
      // the investor's own row (pamm_investors.id), not the manager's
      investorId: null,
      accountName: '',
      quote: null,
      refreshPending: false,
      confirmPending: false,
      deadline: null,
      now: Date.now(),
      timer: null,
    }
  },
  computed: {
    rows() {
      const { quote } = this
      return [
        { key: 'stake', label: 'pamm_early_exit_stake', value: quote.stake },
        {
          key: 'share', label: 'pamm_early_exit_share', value: quote.grossShare, signed: true, colored: true,
        },
        {
          key: 'management', label: 'pamm_early_exit_management_fee', value: this.negate(quote.managementFee), signed: true,
        },
        {
          key: 'performance', label: 'pamm_early_exit_performance_fee', value: this.negate(quote.performanceFee), signed: true,
        },
        { key: 'value', label: 'pamm_early_exit_value', value: quote.valueBeforeFine },
        {
          key: 'fine', label: 'pamm_early_exit_fine', value: this.negate(quote.cancelationFine), signed: true,
        },
      ].filter(row => this.hasValue(row.value))
    },
    expired() {
      return this.deadline !== null && this.now >= this.deadline
    },
    countdown() {
      const seconds = Math.max(0, Math.ceil((this.deadline - this.now) / 1000))
      return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
    },
  },
  beforeDestroy() {
    this.stopClock()
  },
  methods: {
    // Resolves once the dialog is open, or once it is clear there is nothing to show.
    async open(investorId, accountName = '') {
      this.investorId = investorId
      this.accountName = accountName
      const quote = await this.fetchQuote()
      if (!quote) return
      this.showQuote(quote)
      this.visible = true
    },
    async refreshQuote() {
      this.refreshPending = true
      const quote = await this.fetchQuote()
      this.refreshPending = false
      if (quote) {
        this.showQuote(quote)
      } else {
        this.visible = false
      }
    },
    // The quote, or null when there is none; a failed request has already shown the server's message.
    async fetchQuote() {
      return pammApis.cancelInvestment(this.investorId, { mode: 'quote' }).then(res => {
        const quote = readQuote(res)
        if (!quote && res?.data) {
          this.$swal({
            title: this.$t('pamm_early_exit_unavailable'),
            icon: 'error',
            showConfirmButton: false,
            showCloseButton: true,
          })
        }
        return quote
      }).catch(err => {
        console.log(err)
        return null
      })
    },
    async confirm() {
      this.confirmPending = true
      const data = { mode: 'confirm' }
      // sent back when the quote carries one; the confirm commits exactly that quote's figures
      if (this.quote.quoteToken) data.quoteToken = this.quote.quoteToken
      await pammApis.cancelInvestment(this.investorId, data).then(res => {
        if (!res?.data) return
        this.visible = false
        this.$swal({
          title: res.data.message || this.$t('pamm_early_exit_done'),
          icon: 'success',
          timer: 4000,
          showConfirmButton: false,
        })
        this.$emit('exited')
      }).catch(err => {
        // the server's message is already shown; this quote can no longer be confirmed,
        // so offer a new one
        console.log(err)
        this.deadline = Date.now()
        this.now = this.deadline
      }).finally(() => {
        this.confirmPending = false
      })
    },
    showQuote(quote) {
      this.quote = quote
      this.startClock(quote)
    },
    // Counted from when the quote arrived, so a wrong device clock does not matter.
    startClock(quote) {
      this.stopClock()
      const asOf = Date.parse(quote.asOf)
      const expiresAt = Date.parse(quote.expiresAt)
      const validity = expiresAt > asOf ? expiresAt - asOf : QUOTE_VALIDITY_MS
      this.now = Date.now()
      this.deadline = this.now + validity - CONFIRM_MARGIN_MS
      this.timer = setInterval(() => { this.now = Date.now() }, 1000)
    },
    stopClock() {
      clearInterval(this.timer)
      this.timer = null
    },
    reset() {
      this.stopClock()
      this.quote = null
      this.deadline = null
    },
    negate(value) {
      return this.hasValue(value) ? -Number(value) : null
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== ''
    },
    valueClass(value) {
      const number = Number(value)
      if (number > 0) return 'text-success'
      if (number < 0) return 'text-danger'
      return ''
    },
    formatAmount(value, signed = false) {
      if (!this.hasValue(value)) return '-'
      // + 0 turns -0 into 0
      const number = Math.round(Number(value) * 100) / 100 + 0
      return `${signed && number > 0 ? '+' : ''}${numberWithCommas(number.toFixed(2))} $`
    },
    formatDays(days) {
      return Math.round(Number(days) * 10) / 10
    },
  },
}
</script>

<style lang="scss" scoped>
.pamm-exit__payout {
  padding: 0.75rem 1rem;
  border-radius: 0.428rem;
  background-color: rgba(40, 199, 111, 0.08);
}

.dark-layout .pamm-exit__payout {
  background-color: rgba(40, 199, 111, 0.12);
}
</style>
